'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterControlsProps {
  dietaryFilter: 'all' | 'veg' | 'non-veg';
  onDietaryFilterChange: (filter: 'all' | 'veg' | 'non-veg') => void;
  currentOfferFilter: boolean;
  onCurrentOfferFilterChange: (value: boolean) => void;
  sortBy: 'popularity' | 'price-low' | 'price-high' | 'name';
  onSortChange: (sort: 'popularity' | 'price-low' | 'price-high' | 'name') => void;
  priceRange: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const FilterControls = ({
  dietaryFilter,
  onDietaryFilterChange,
  currentOfferFilter,
  onCurrentOfferFilterChange,
  sortBy,
  onSortChange,
  priceRange,
  onPriceRangeChange,
  searchQuery,
  onSearchChange,
}: FilterControlsProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      {/* Mobile Toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground focus-ring"
        aria-expanded={isExpanded}
      >
        <span className="flex items-center space-x-2">
          <Icon name="AdjustmentsHorizontalIcon" size={20} />
          <span>Filters & Sort</span>
        </span>
        <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} />
      </button>

      {/* Filter Content */}
      <div
        className={`${
          isExpanded ? 'block' : 'hidden'
        } lg:block px-4 py-4 space-y-4 border-t border-border lg:border-t-0`}
      >
        {/* Dietary Preferences */}
        <div>
          <label className="text-caption font-medium text-foreground mb-2 block">
            Dietary Preference
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'All Items', icon: 'Squares2X2Icon' },
              { value: 'veg', label: 'Vegetarian', icon: 'CheckCircleIcon' },
              { value: 'non-veg', label: 'Non-Veg', icon: 'XCircleIcon' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onDietaryFilterChange(option.value as 'all' | 'veg' | 'non-veg')}
                className={`flex items-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium transition-smooth focus-ring ${
                  dietaryFilter === option.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-background text-foreground border border-border hover:bg-muted'
                }`}
              >
                <Icon name={option.icon} size={16} />
                <span>{option.label}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => onCurrentOfferFilterChange(!currentOfferFilter)}
              className={`flex items-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium transition-smooth focus-ring ${
                currentOfferFilter
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-background text-foreground border border-border hover:bg-muted'
              }`}
            >
              <Icon name="TagIcon" size={16} />
              <span>Current Offer</span>
            </button>
          </div>
        </div>

        {/* Search and Sort By */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <label className="text-caption font-medium text-foreground mb-2 block">Search</label>
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search menu items..."
                className="w-full rounded-lg border border-border bg-background pl-10 pr-4 py-2 text-sm text-foreground transition-smooth focus-ring hover:border-primary"
              />
            </div>
          </div>
          {/* Sort By */}
          <div className="flex-1">
            <label className="text-caption font-medium text-foreground mb-2 block">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) =>
                onSortChange(e.target.value as 'popularity' | 'price-low' | 'price-high' | 'name')
              }
              className="w-full rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground transition-smooth focus-ring hover:border-primary"
            >
              <option value="popularity">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </select>
          </div>
        </div>

        {/* Price Range */}
        <div>
          <label className="text-caption font-medium text-foreground mb-2 block">
            Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="range"
              min="0"
              max="1000"
              value={priceRange[1]}
              onChange={(e) => onPriceRangeChange([priceRange[0], parseInt(e.target.value)])}
              className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-data text-sm font-medium text-foreground w-16 text-right">
              ₹{priceRange[1]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;
